import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, effect, inject, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { BudgetMessagingConstants } from '../../../../core/constants/messaging.constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { LocalStorageKeyConstants, PaginationConstants } from '../../../../core/constants/constants';
import { DataEntityTypes, PropertyStatuses } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';
import { BudgetListSortOptions } from '../../../../core/enums/sort-options.enums';
import { BudgetModel } from '../../../../core/models/data/budget.model';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { EnumUtilities } from '../../../../core/utilities/enum.utilities';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { BudgetSearchFilter } from '../../components/budget-search-filter/budget-search-filter';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-budget-selection',
  standalone: true,
  imports: [
    PageTitleComponent,
    CommonModule,
    FormsModule,
    RouterLink,
    FontAwesomeModule,
    ConfirmationModalComponent,
    IfHasPermissionDirective,
    InfoModalComponent,
    InViewPortDirective,
    PaginationComponent,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    TooltipDirective,
    SortDropdownComponent,
    StatusBadgeComponent,
    NoValuePipe,
    OrgDatePipe,
    BudgetSearchFilter
  ],
  templateUrl: './budget-selection.page.html',
  styleUrl: './budget-selection.page.scss',
  host: { 'style': 'display:contents' }
})
export class BudgetSelectionPage extends BaseListPageClass implements OnInit {

  //#region Dependencies

  private financialService = inject(FinancialService);
  public userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);
  private changeDetector = inject(ChangeDetectorRef);

  //#endregion Dependencies

  //#region State

  public budgetList = signal<BudgetModel[]>([]);
  public skeletonBudgets: BudgetModel[] = [];

  public targetedBudget?: BudgetModel;
  public isSubmitting = false;
  public confirmationConfirmFunction: (note?: string) => void = () => {};
  public confirmationCancelFunction: () => void = () => {};

  public BudgetMessagingConstants = BudgetMessagingConstants;
  public FinancialsPermission = FinancialsPermission;
  public DataEntityTypes = DataEntityTypes;
  public PropertyStatuses = PropertyStatuses;
  public budgetListSortOptions = BudgetListSortOptions;
  public enumUtilities = EnumUtilities;

  @ViewChild('budgetListConfirmationModalRef') budgetListConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('budgetListInfoModalRef') budgetListInfoModalRef!: InfoModalComponent;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  //#endregion State

  //#region Lifecycle

  constructor() {
    super();

    effect(() => {
      const org = this.userSessionService.activeOrganization();
      if (org && this.userSessionService.hasInitializedOrganization()) {
        untracked(() => this.loadBudgets(false));
      }
    });
  }

  public ngOnInit(): void {
    this.createSkeletonBudgets();
    this.orderBy = this.userSessionService.budgetsOrderBy;
    this.sortOptions = [...ListConstants.BUDGETS_SORT_OPTIONS()];
    // Initial load is driven by the budget-search-filter's filterApplied event
    // (fired during its ngOnInit) so the persisted filter is applied on entry.
  }

  //#endregion Lifecycle

  //#region Data Loading

  public get displayBudgets(): BudgetModel[] {
    if (this.isLoading && (!this.budgetList() || this.budgetList().length === 0)) {
      return this.skeletonBudgets;
    }
    return this.budgetList() || [];
  }

  public async loadBudgets(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (!append) { this.resetPagination(); }

    const organizationId = this.userSessionService.activeOrganization()?.organizationId;
    this.userSessionService.budgetsFilter.organizationIds = organizationId ? [organizationId] : [];

    const response: ServiceResultModel<PaginatedListModel<BudgetModel>> =
      await this.financialService.searchBudgets(
        this.userSessionService.budgetsFilter,
        this.orderBy,
        this.skip(),
        this.take
      );

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append) {
        this.budgetList.set(this.budgetList().concat(response.data.data));
      } else {
        this.budgetList.set(response.data.data);
      }
    }

    this.isLoading = false;
    this.changeDetector.detectChanges();
  }

  public async loadMore(): Promise<void> {
    this.loadBudgets(true);
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }

  public clearSearch(): void {
    this.userSessionService.budgetsFilter.searchTerm = '';
  }

  public filterApplied(): void {
    this.loadBudgets(false);
  }

  //#endregion Data Loading

  //#region Sorting & Pagination

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [BudgetListSortOptions, BudgetListSortOptions]> = {
      'Name': [BudgetListSortOptions.NameAsc, BudgetListSortOptions.NameDesc],
      'Year': [BudgetListSortOptions.YearAsc, BudgetListSortOptions.YearDesc],
      'Frequency': [BudgetListSortOptions.FrequencyAsc, BudgetListSortOptions.FrequencyDesc],
      'Status': [BudgetListSortOptions.StatusAsc, BudgetListSortOptions.StatusDesc],
      'Modified': [BudgetListSortOptions.LastModifiedAsc, BudgetListSortOptions.LastModifiedDesc]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      ?? [BudgetListSortOptions.LastModifiedAsc, BudgetListSortOptions.LastModifiedDesc];

    let sortDirection: BudgetListSortOptions;
    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(value: string): void {
    this.orderBy = value;
    this.userSessionService.budgetsOrderBy = value;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.BUDGETS_ORDERBY(), this.orderBy);
    this.loadBudgets(false);
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  //#endregion Sorting & Pagination

  //#region Delete Budget

  public confirmBudgetDeletion(budget: BudgetModel): void {
    this.targetedBudget = budget;

    const confirmationMessage = BudgetMessagingConstants.BUDGET_DELETE_CONFIRMATION()
      .replaceValuesWithObject({ name: budget.name ?? '' });

    this.confirmationConfirmFunction = this.confirmedBudgetDeletion.bind(this);
    this.confirmationCancelFunction = this.cancelBudgetDeletion.bind(this);

    this.budgetListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.budgetListConfirmationModalRef.message.set(confirmationMessage);
    this.budgetListConfirmationModalRef.enableNotes.set(true);
    this.budgetListConfirmationModalRef.isNoteRequired.set(false);

    this.bootstrapService.manageModal('#budgetListConfirmationModal', ModalActions.Show);
  }

  public async confirmedBudgetDeletion(note?: string): Promise<void> {
    if (!this.targetedBudget) { return; }

    this.isSubmitting = true;
    const response = await this.financialService.deleteBudget(this.targetedBudget.budgetId, note);

    if (response.success === false) {
      this.budgetListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      this.budgetListInfoModalRef.modalType.set(ModalTypes.Success);
      await this.loadBudgets(false);
    }

    this.isSubmitting = false;
    this.budgetListInfoModalRef.message.set(response.message);
    this.bootstrapService.manageModal('#budgetListInfoModal', ModalActions.Show);
    this.bootstrapService.manageModal('#budgetListConfirmationModal', ModalActions.Hide);
  }

  public cancelBudgetDeletion(): void {
    this.bootstrapService.manageModal('#budgetListConfirmationModal', ModalActions.Hide);
  }

  //#endregion Delete Budget

  //#region Private Helpers

  private createSkeletonBudgets(): void {
    this.skeletonBudgets = Array.from({ length: this.take }, (_, i) => ({
      budgetId: -1 - i,
      organizationId: -1,
      name: 'Loading...',
      year: 0,
      budgetFrequencyId: 'Yearly' as any,
      currency: { currencyId: -1, name: '', code: '', symbol: '', minorUnits: 100 } as any,
      lastModified: '',
      status: 'Active' as any,
      budgetLineItems: []
    } as BudgetModel));
  }

  //#endregion Private Helpers
}
